import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Plus, CheckCircle, Clock, XCircle, AlertCircle, DollarSign, Calendar, MapPin } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import SortableHeader, { useTableSort, useColumnFilters, usePagination, TablePagination } from '../components/table/SortableHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_STYLES = {
    pending: { bg: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
    approved: { bg: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
    rejected: { bg: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
    expired: { bg: 'bg-stone-50 text-stone-600 border-stone-200', icon: AlertCircle },
};

function RegistrationModal({ open, onClose, onSave }) {
    const { getAuthHeader } = useAuth();
    const [form, setForm] = useState({ account_name: '', account_domain: '', opportunity_description: '', estimated_value: '', expected_close_date: '', territory: '' });
    const [loading, setLoading] = useState(false);

    const handleSave = async () => {
        if (!form.account_name || !form.opportunity_description) return toast.error('Account name and description required');
        setLoading(true);
        try {
            const payload = { ...form, estimated_value: parseFloat(form.estimated_value) || undefined };
            const res = await axios.post(`${API}/deal-registrations`, payload, { headers: getAuthHeader() });
            toast.success('Deal registration submitted!');
            onSave(res.data);
            onClose();
            setForm({ account_name: '', account_domain: '', opportunity_description: '', estimated_value: '', expected_close_date: '', territory: '' });
        } catch { toast.error('Failed to submit registration'); }
        finally { setLoading(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Register New Deal</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-stone-500 -mt-2">Protect your deal by registering it. Approval typically takes 2-3 business days.</p>
                <div className="space-y-4 mt-2">
                    {[
                        { label: 'Account Name *', key: 'account_name', placeholder: 'Acme Corporation' },
                        { label: 'Account Domain', key: 'account_domain', placeholder: 'acme.com' },
                        { label: 'Territory', key: 'territory', placeholder: 'Northeast US' },
                        { label: 'Estimated Value ($)', key: 'estimated_value', placeholder: '50000', type: 'number' },
                        { label: 'Expected Close Date', key: 'expected_close_date', type: 'date' },
                    ].map(f => (
                        <div key={f.key}>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">{f.label}</label>
                            <input type={f.type || 'text'} value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder}
                                data-testid={`reg-${f.key}-input`}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20 focus:border-stone-400" />
                        </div>
                    ))}
                    <div>
                        <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Opportunity Description *</label>
                        <textarea value={form.opportunity_description} onChange={e => setForm({ ...form, opportunity_description: e.target.value })} rows={3}
                            placeholder="Describe the opportunity and why you should be the registered rep..."
                            data-testid="reg-description-input"
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20" />
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                    <button onClick={onClose} className="px-4 py-2 text-sm border border-stone-200 rounded-lg hover:bg-stone-50 text-stone-700">Cancel</button>
                    <button onClick={handleSave} disabled={loading} data-testid="reg-submit-btn"
                        className="px-4 py-2 text-sm bg-stone-900 text-white rounded-lg hover:bg-stone-800 disabled:opacity-50">
                        {loading ? 'Submitting...' : 'Submit Registration'}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default function DealRegistration() {
    const { getAuthHeader, user } = useAuth();
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const res = await axios.get(`${API}/deal-registrations`, { headers: getAuthHeader() });
            setRegistrations(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch {} finally { setLoading(false); }
    };

    const handleApprove = async (id, status) => {
        try {
            const res = await axios.put(`${API}/deal-registrations/${id}`, { status }, { headers: getAuthHeader() });
            setRegistrations(prev => prev.map(r => r.registration_id === id ? res.data : r));
            toast.success(`Registration ${status}`);
        } catch { toast.error('Failed to update'); }
    };

    const handleSave = (saved) => setRegistrations(prev => [saved, ...prev]);

    const formatVal = v => v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K` : `$${v}`;

    const REG_COLS = [
        { key: 'account_name', label: 'Company' },
        { key: 'territory', label: 'Territory' },
        { key: 'rep_name', label: 'Rep' },
        { key: 'estimated_value', label: 'Est. Value' },
        { key: 'expected_close_date', label: 'Close Date' },
        { key: 'expiry_date', label: 'Expiry' },
        { key: 'commission_percentage', label: 'Commission' },
        { key: 'status', label: 'Status' },
    ];
    const { columnFilters, setColumnFilter, filteredData: filteredRegs } = useColumnFilters(registrations);
    const { sortColumn, sortDirection, handleSort, sortedData: sortedRegs } = useTableSort(filteredRegs, { numericKeys: ['estimated_value', 'commission_percentage'] });
    const { page, pageSize, setPage, setPageSize, pageData: pagedRegs, totalPages, total: totalRegs } = usePagination(sortedRegs, 25);

    const stats = {
        total: registrations.length,
        pending: registrations.filter(r => r.status === 'pending').length,
        approved: registrations.filter(r => r.status === 'approved').length,
        value: registrations.filter(r => r.status === 'approved').reduce((s, r) => s + (r.estimated_value || 0), 0),
    };

    return (
        <div className="space-y-6">
            {/* Header stats */}
            <div className="grid grid-cols-4 gap-4">
                {[
                    { label: 'Total Registrations', value: stats.total, icon: Shield, color: 'text-stone-700', bg: 'bg-stone-100' },
                    { label: 'Pending Review', value: stats.pending, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
                    { label: 'Approved', value: stats.approved, icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { label: 'Protected Value', value: formatVal(stats.value), icon: DollarSign, color: 'text-violet-600', bg: 'bg-violet-50' },
                ].map((m, i) => (
                    <div key={i} className="bg-white rounded-xl border border-stone-200 p-5 card-hover">
                        <div className={`w-9 h-9 ${m.bg} rounded-xl flex items-center justify-center mb-3`}>
                            <m.icon className={`w-4 h-4 ${m.color}`} />
                        </div>
                        <p className={`text-2xl font-bold ${m.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{m.value}</p>
                        <p className="text-xs text-stone-500 mt-0.5">{m.label}</p>
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center">
                        <Shield className="w-4 h-4 text-stone-700" />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-stone-900">Deal Registrations</p>
                        <p className="text-xs text-stone-400">Protect your territory and claim commission</p>
                    </div>
                </div>
                <button onClick={() => setShowModal(true)} data-testid="new-registration-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 shadow-sm">
                    <Plus className="w-4 h-4" /> Register Deal
                </button>
            </div>

            <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="registrations-table">
                {loading ? (
                    <div className="p-12 text-center text-stone-400">Loading...</div>
                ) : registrations.length === 0 ? (
                    <div className="p-12 text-center">
                        <Shield className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                        <p className="text-stone-500 text-sm mb-3">No deal registrations yet</p>
                        <button onClick={() => setShowModal(true)} className="text-stone-700 text-sm">Register your first deal</button>
                    </div>
                ) : (
                    <>
                    <table className="w-full">
                        <thead>
                            <tr className="bg-stone-50 border-b border-stone-200">
                                {REG_COLS.map(col => (
                                    <SortableHeader
                                        key={col.key}
                                        label={col.label}
                                        columnKey={col.key}
                                        sortColumn={sortColumn}
                                        sortDirection={sortDirection}
                                        onSort={handleSort}
                                        filterValue={columnFilters[col.key]}
                                        onFilter={setColumnFilter}
                                        allData={registrations}
                                    />
                                ))}
                                <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                            {pagedRegs.map(r => {
                                const styles = STATUS_STYLES[r.status] || STATUS_STYLES.pending;
                                const Icon = styles.icon;
                                return (
                                    <tr key={r.registration_id} className="hover:bg-stone-50" data-testid={`reg-row-${r.registration_id}`}>
                                        <td className="px-4 py-4">
                                            <p className="text-sm font-medium text-stone-900">{r.account_name}</p>
                                            {r.account_domain && <p className="text-xs text-stone-400">{r.account_domain}</p>}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-stone-600">{r.territory || '—'}</td>
                                        <td className="px-4 py-4 text-sm text-stone-600">{r.rep_name || '—'}</td>
                                        <td className="px-4 py-4 text-sm font-medium text-stone-700">{r.estimated_value ? formatVal(r.estimated_value) : '—'}</td>
                                        <td className="px-4 py-4 text-sm text-stone-500">{r.expected_close_date || '—'}</td>
                                        <td className="px-4 py-4 text-sm text-stone-500">{r.expiry_date || '—'}</td>
                                        <td className="px-4 py-4 text-sm font-medium text-emerald-600">{r.commission_percentage ? `${r.commission_percentage}%` : '—'}</td>
                                        <td className="px-4 py-4">
                                            <span className={`text-xs font-medium px-2 py-1 rounded-full border capitalize flex items-center gap-1 w-fit ${styles.bg}`}>
                                                <Icon className="w-3 h-3" />{r.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-4">
                                            {r.status === 'pending' && user?.role === 'admin' && (
                                                <div className="flex gap-2">
                                                    <button onClick={() => handleApprove(r.registration_id, 'approved')} data-testid={`approve-${r.registration_id}`}
                                                        className="text-xs px-2 py-1 bg-emerald-50 text-emerald-600 rounded-md hover:bg-emerald-100 border border-emerald-200">Approve</button>
                                                    <button onClick={() => handleApprove(r.registration_id, 'rejected')}
                                                        className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded-md hover:bg-red-100 border border-red-200">Reject</button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <TablePagination
                        page={page}
                        totalPages={totalPages}
                        total={totalRegs}
                        pageSize={pageSize}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                    />
                    </>
                )}
            </div>

            <RegistrationModal open={showModal} onClose={() => setShowModal(false)} onSave={handleSave} />
        </div>
    );
}
